import { useState, useCallback, useRef, useEffect } from "react";
import { AnimatePresence } from "motion/react";
import { Header } from "./components/Header";
import { ProjectGrid } from "./components/ProjectGrid";
import { ContactSection } from "./components/ContactSection";
import { ProjectModal } from "./components/ProjectModal";
import { ImagePreview } from "./components/ImagePreview";
import { useKeyboardNavigation } from "./hooks/useKeyboardNavigation";
import { projects } from "./data/projects";
import type { Project } from "./types/Project";
import "./index.css";

export function App() {
  const [activeProject, setActiveProject] = useState<Project | null>(null);
  const [focusedIndex, setFocusedIndex] = useState(0);
  const [selectedImage, setSelectedImage] = useState(1);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const gridRef = useRef<HTMLDivElement>(null);

  const openProject = useCallback((project: Project) => {
    setActiveProject(project);
    setSelectedImage(1);
  }, []);

  const closeProject = useCallback(() => {
    setActiveProject(null);
  }, []);

  useKeyboardNavigation({
    projects,
    focusedIndex,
    setFocusedIndex,
    activeProject,
    imagePreview,
    openProject,
    closeProject,
    setImagePreview,
  });

  // load font
  useEffect(() => {
    const link = document.createElement("link");
    link.rel = "stylesheet";
    link.href =
      "https://fonts.googleapis.com/css2?family=Space+Mono:wght@400;700&display=swap";
    document.head.appendChild(link);
    return () => {
      document.head.removeChild(link);
    };
  }, []);

  return (
    <div className="bg-[#0f0f0f] text-white min-h-screen selection:bg-white selection:text-black">
      <main className="px-6 sm:px-12 lg:px-8 pt-8 pb-8 max-w-6xl mx-auto">
        <Header />

        <ProjectGrid
          ref={gridRef}
          projects={projects}
          onOpenProject={openProject}
          onFocusProject={setFocusedIndex}
        />

        <ContactSection />
      </main>

      <AnimatePresence>
        {activeProject && (
          <ProjectModal
            project={activeProject}
            selectedImage={selectedImage}
            onClose={closeProject}
            onSelectImage={setSelectedImage}
            onImagePreview={setImagePreview}
          />
        )}
      </AnimatePresence>

      <AnimatePresence>
        {imagePreview && (
          <ImagePreview
            imageUrl={imagePreview}
            onClose={() => setImagePreview(null)}
          />
        )}
      </AnimatePresence>
    </div>
  );
}
